/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Box from '@components/Atoms/Box';
import { useParams } from 'react-router-dom';
import Typography from '@components/Atoms/Typography';
import DateDisplay from '@components/Atoms/DateDisplay';
import Tooltip from '@components/Atoms/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@components/Atoms/IconButton';
import { useSectionStudentWithFilter } from '@reducers/sectionStudent/hooks';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import ShowIf from '@components/Atoms/ShowIf';
import { compareStudentsByName, formatStudentName } from '@utils/studentName';
import { useGetTestletSessionsWithFilter, useStudentSections } from '../hooks';
import { StudentTestletSessionStatusLabels, Testlet, TestletStatus } from '../types';
import { useSessionManagement } from '../contexts/SessionManagementProvider';
import ActiveCellRenderer from './ActiveCellRenderer';

// Define cell renderer outside the component
const StatusCellRenderer = ({
  value,
  params,
}: {
  value: string;
  params: GridRenderCellParams<{
    started_at: string;
    duration: {
      original: number;
      extended: number;
      total: number;
      remaining: number;
    };
  }>;
}) => {
  const status = value;
  let color = 'inherit';

  if (status === 'Completed' && params.row.started_at === '--') {
    color = '#C62828'; // 5.09:1 on white
  } else if (status === 'Completed') {
    color = 'green';
  } else if (status === 'In Progress') {
    color = 'blue';
  } else if (status === 'Not Started') {
    color = '#666666'; // 4.61:1 on white
  }
  if (status === 'Completed' && params.row.started_at === '--') {
    return (
      <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <span style={{ color }}>Missed</span>
        <Tooltip title="This test session was never started" placement="top" arrow>
          <IconButton size="small" aria-label="This test session was never started">
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>
    );
  }
  if (status === 'Paused') {
    color = '#8B5700'; // dark amber, 5.44:1 on white
    return (
      <Typography sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span style={{ color }}>{status}</span>
        <span>
          ({params.row.duration.remaining}
          &nbsp; min remaining)
        </span>
      </Typography>
    );
  }

  return (
    <Typography sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <span style={{ color }}>{status}</span>
    </Typography>
  );
};
const DurationCellRenderer = ({
  value,
}: {
  value: { original: number; extended: number };
}) => {
  const { original, extended } = value;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        width: '100%',
        alignItems: 'center',
      }}
    >
      <Typography variant="body2" sx={{ color: 'text.primary' }}>
        {original + (extended ?? 0)} min
      </Typography>
      {extended > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ({extended} min extended)
        </Typography>
      )}
    </Box>
  );
};
const TestletStudentSessions: React.FC<{ testlet: Testlet }> = ({ testlet }) => {
  const dataGridContainerRef = useRef<HTMLDivElement>(null);
  const { sectionId } = useParams();
  const testletSessions = useGetTestletSessionsWithFilter(testlet.id);
  const students = useSectionStudentWithFilter();
  const { data: studentSections } = useStudentSections();
  const { selectedStudents, setSelectedStudents, isResettingTestletSession } =
    useSessionManagement();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 35 });

  const isLocked =
    testlet.status === TestletStatus.LOCKED || testlet.status === TestletStatus.COMPLETED;

  // Define column definitions for MUI DataGrid
  const columnDefs = useMemo<GridColDef[]>(() => {
    const columns: GridColDef[] = [
      {
        field: 'student',
        headerName: 'Student',
        sortable: true,
        filterable: true,
        flex: 1,
        minWidth: 175,
        renderCell: (params) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'duration',
        headerName: 'Duration',
        sortable: true,
        filterable: true,
        minWidth: 150,
        width: 150,
        align: 'center',
        renderCell: (params) => <DurationCellRenderer value={params.value} />,
      },
      {
        field: 'scores',
        headerName: 'Scores',
        sortable: true,
        filterable: true,
        minWidth: 90,
        width: 90,
        align: 'center',
      },
      {
        field: 'active',
        headerName: 'Active',
        sortable: false,
        filterable: true,
        minWidth: 75,
        width: 75,
        align: 'center',
        renderCell: (params) => <ActiveCellRenderer testletSession={params.row} />,
        hide: isLocked,
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: true,
        filterable: true,
        minWidth: 100,
        width: 150,
        align: 'center',
        renderCell: (params) => (
          <StatusCellRenderer value={params.value} params={params} />
        ),
      },
      {
        field: 'started_at',
        headerName: 'Started At',
        sortable: true,
        filterable: true,
        minWidth: 150,
        align: 'center',
        renderCell: (params) => (
          <DateDisplay
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'transparent',
              border: 'none',
            }}
            startDate={params.value}
            showTime
            size="small"
          />
        ),
      },
      {
        field: 'completed_at',
        headerName: 'Completed At',
        sortable: true,
        filterable: true,
        minWidth: 150,
        align: 'center',
        renderCell: (params) => (
          <DateDisplay
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'transparent',
              border: 'none',
            }}
            startDate={params.value}
            showTime
            size="small"
          />
        ),
      },
    ];
    return isLocked ? columns.filter((col) => col.field !== 'active') : columns;
  }, [isLocked]);

  // Process row data in the same way
  const rowData = useMemo(() => {
    // first lets merge students with testletSessions
    const mergedData = students.map((student) => {
      const session = testletSessions?.find((ts) => ts.user_id === student.id);
      const sections: string[] = studentSections ? studentSections[student.id] || [] : [];
      const name = (
        <Typography
          sx={{ fontSize: '1em', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          {formatStudentName(student)}{' '}
          <ShowIf If={sections.length > 1}>
            <Tooltip
              sx={{ cursor: 'pointer' }}
              placement="right"
              slotProps={{
                tooltip: {
                  sx: { fontSize: '1em', whiteSpace: 'nowrap', maxWidth: 'none' },
                },
              }}
              title={
                <>
                  <span>Student has access to this test in the following sections:</span>
                  {sections
                    .filter((s) => s[0] !== sectionId)
                    .map((s) => (
                      <p key={s[0]}> - {s[1]}</p>
                    ))}
                  <span>
                    The student will only be marked as &quot;Missed&quot; when all
                    sections have completed their tests
                  </span>
                </>
              }
            >
              <AutoAwesomeMotionIcon fontSize="small" />
            </Tooltip>
          </ShowIf>
        </Typography>
      );
      return {
        ...student,
        testletSession: session || null,
        name,
      };
    });
    return (
      mergedData
        ?.sort((a, b) => compareStudentsByName(a, b))
        .map(({ testletSession, id, name }) => ({
          id,
          student: name,
          started_at: testletSession?.started_at
            ? new Date(testletSession.started_at).toLocaleString()
            : '--',
          completed_at: testletSession?.completed_at
            ? new Date(testletSession.completed_at).toLocaleString()
            : '--',
          scores:
            testletSession &&
            testletSession.score != null &&
            testletSession.started_at != null
              ? `${testletSession?.score} / ${testletSession.max_score}`
              : '-- / --',
          status: testletSession
            ? StudentTestletSessionStatusLabels[testletSession.status]
            : 'Not Started',
          active: testletSession ? testletSession.active : false,
          duration: testletSession
            ? {
                original: testlet.duration,
                extended: testletSession.time_settings.extended_duration,
                total:
                  testlet.duration +
                  (testletSession.time_settings.extended_duration ?? 0),
                remaining: Math.floor(
                  testlet.duration +
                    (testletSession.time_settings.extended_duration ?? 0) -
                    (testletSession.time_settings.time_elapsed ?? 0),
                ),
              }
            : {
                original: testlet.duration,
                extended: 0,
                total: testlet.duration,
                remaining: testlet.duration,
              },
        })) || []
    );
  }, [sectionId, studentSections, students, testlet.duration, testletSessions]);

  useEffect(() => {
    const container = dataGridContainerRef.current;
    if (!container) {
      return undefined;
    }
    const syncVirtualScrollerTabIndex = () => {
      const virtualScrollers = container.querySelectorAll('.MuiDataGrid-virtualScroller');
      virtualScrollers.forEach((virtualScroller) => {
        virtualScroller.setAttribute('tabindex', '0');
      });
    };

    syncVirtualScrollerTabIndex();
    const observer = new MutationObserver(syncVirtualScrollerTabIndex);
    observer.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex'],
      childList: true,
    });

    return () => observer.disconnect();
  }, [rowData.length]);

  return (
    <Box sx={{ mt: 2 }} ref={dataGridContainerRef}>
      <DataGrid
        rows={rowData}
        columns={columnDefs}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 35, 50, 100]}
        checkboxSelection
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setSelectedStudents(newRowSelectionModel as unknown as number[]);
        }}
        loading={isResettingTestletSession}
        rowSelectionModel={selectedStudents as any}
        hideFooterSelectedRowCount
        hideFooter
        aria-label="Student testlet sessions"
        sx={{
          '& .MuiDataGrid-virtualScroller': {
            overflow: 'hidden !important',
          },
        }}
        disableColumnMenu
      />
    </Box>
  );
};

export default TestletStudentSessions;
